import { ChangeDetectionStrategy, Component, computed, inject, signal } from '@angular/core';
import { Router } from '@angular/router';
import { CapabilitiesService } from '@sisoma/auth';
import { Button, Icon, InputControl, Modal, SearchInput } from '@sisoma/ui';

import { PartnersData, type Partner } from '../../data/partners.data';

/**
 * The partner switcher — a shell control (mounted in the sidenav footer) that
 * shows the active partner and opens a searchable "Select a partner" modal,
 * modeled on the legacy Select-a-Creator picker. Choosing a partner sets it as
 * active (which the nav, capability gating, and the `creatorSub` header all
 * react to) and lands on the dashboard.
 *
 * The dialog is `<ui-modal>` and the search box `<ui-search-input>` — this
 * component predated both primitives by a day and hand-rolled them; the swap
 * (2026-08-26) also moved it from a bespoke 34rem panel to the library's
 * canonical 400px. Escape/backdrop/focus-trap behaviour now belongs to the
 * modal; `open` is two-way bound so every close path reports back.
 */
@Component({
  selector: 'app-partner-switcher',
  imports: [Button, Icon, InputControl, Modal, SearchInput],
  templateUrl: './partner-switcher.html',
  styleUrl: './partner-switcher.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PartnerSwitcher {
  private readonly capabilities = inject(CapabilitiesService);
  private readonly router = inject(Router);
  protected readonly data = inject(PartnersData);

  /** Two-way bound to the modal, so Escape and the close button report back. */
  protected readonly open = signal(false);
  /** The current search text. */
  protected readonly query = signal('');

  protected readonly activeId = this.capabilities.activePartnerId;

  private readonly partners = computed<Partner[]>(() => this.data.partners.value() ?? []);

  /** Label for the trigger: the active partner's name, or a prompt. */
  protected readonly activeName = computed(() => {
    const id = this.activeId();
    if (!id) {
      return 'Select partner';
    }
    return this.partners()?.find((partner) => partner.id === id)?.name ?? 'Partner';
  });

  /** Partners matching the search box (case-insensitive on name / owner email). */
  protected readonly filtered = computed<Partner[]>(() => {
    const query = this.query()?.trim()?.toLowerCase();
    const all = this.partners();
    if (!query) {
      return all;
    }
    return all.filter(
      (partner) =>
        partner.name.toLowerCase().includes(query) ||
        (partner.ownerEmail?.toLowerCase().includes(query) ?? false),
    );
  });

  protected openPicker(): void {
    this.query.set('');
    this.open.set(true);
  }

  protected select(partner: Partner): void {
    // Sets the active partner (persisted in @sisoma/auth) and re-derives caps,
    // nav and the creatorSub header from it.
    this.capabilities.setActivePartnerId(partner.id);
    this.open.set(false);
    void this.router.navigate(['dashboard']);
  }
}
